import { supabase } from './supabase.js'

/**
 * Fasting. A timer and a trend, which is all he asked for — no plans, no
 * tips, no streaks.
 *
 * While a fast runs, only its start is stored. How long it has been is worked
 * out when the screen opens, so closing the app, losing signal or restarting
 * the phone cannot lose a fast. Nothing is counting, so nothing can stop.
 *
 * The target lives on the fast rather than in settings. Changing his usual
 * window should not rewrite what last month was aiming at.
 */

export const DEFAULT_TARGET = 16

const HOUR = 3600000

/** The one running fast, or null. The partial unique index makes "one" true. */
export async function getOpenFast() {
  const { data, error } = await supabase
    .from('fasts')
    .select('*')
    .is('ended_at', null)
    .order('started_at', { ascending: false })
    .limit(1)
    .maybeSingle()
  if (error) throw error
  return data ?? null
}

export async function getFasts(limit = 60) {
  const { data, error } = await supabase
    .from('fasts')
    .select('*')
    .not('ended_at', 'is', null)
    .order('started_at', { ascending: false })
    .limit(limit)
  if (error) throw error
  return data ?? []
}

/* ── The target notification ─────────────────────────────────
 * A reminder row, which the five-minute sweep already knows how to send.
 * No new Worker code, no second scheduler, and it dies with the fast.
 * ──────────────────────────────────────────────────────────── */

function targetAt(startedAt, hours) {
  return new Date(new Date(startedAt).getTime() + hours * HOUR).toISOString()
}

async function clearAlarm(fastId) {
  /* Only the unsent one. A notification that already arrived is history and
     deleting it would be rewriting the past. */
  await supabase
    .from('reminders')
    .delete()
    .eq('target_type', 'fast')
    .eq('target_id', fastId)
    .is('sent_at', null)
}

async function setAlarm(fast) {
  await clearAlarm(fast.id)
  const at = targetAt(fast.started_at, Number(fast.target_hours))
  /* A target already in the past would fire the moment it is written, which
     is a notification telling him something he can see on the screen. */
  if (new Date(at) <= new Date()) return
  await supabase.from('reminders').insert({
    body: Number(fast.target_hours) + 'h fast complete',
    remind_at: at,
    target_type: 'fast',
    target_id: fast.id,
  })
}

export async function startFast({ startedAt, targetHours = DEFAULT_TARGET } = {}) {
  const { data, error } = await supabase
    .from('fasts')
    .insert({
      started_at: startedAt ?? new Date().toISOString(),
      target_hours: targetHours,
    })
    .select()
    .single()
  if (error) {
    /* The partial unique index speaks in Postgres. Say it in his words. */
    if (error.code === '23505') throw new Error('A fast is already running.')
    throw error
  }
  /* Not awaited. The alarm is hours away; making him watch two more round
     trips before the ring appears is the wrong trade. */
  setAlarm(data).catch(() => {})
  return data
}

/** A fast that already happened, typed in after the fact. No alarm: it is over. */
export async function logFast({ startedAt, endedAt, targetHours = DEFAULT_TARGET }) {
  if (!startedAt || !endedAt) throw new Error('Both a start and an end are needed.')
  if (new Date(endedAt) <= new Date(startedAt)) {
    throw new Error('The end has to come after the start.')
  }
  const { data, error } = await supabase
    .from('fasts')
    .insert({ started_at: startedAt, ended_at: endedAt, target_hours: targetHours })
    .select()
    .single()
  if (error) throw error
  return data
}

export async function endFast(fast, endedAt) {
  const when = endedAt ?? new Date().toISOString()
  const { data, error } = await supabase
    .from('fasts')
    .update({ ended_at: when })
    .eq('id', fast.id)
    .select()
    .single()
  if (error) throw error
  /* Not awaited, for the same reason as the start. If it loses the race the
     sweep sends one notification about a fast he has already ended, which is
     a smaller cost than a second of waiting every single time. */
  clearAlarm(fast.id).catch(() => {})
  return data
}

/** Fixing a time he forgot to record. The alarm follows a moved start. */
export async function updateFast(fast, patch) {
  const { data, error } = await supabase
    .from('fasts')
    .update(patch)
    .eq('id', fast.id)
    .select()
    .single()
  if (error) throw error
  if (data.ended_at) clearAlarm(data.id).catch(() => {})
  else setAlarm(data).catch(() => {})
  return data
}

export async function deleteFast(id) {
  await clearAlarm(id).catch(() => {})
  const { error } = await supabase.from('fasts').delete().eq('id', id)
  if (error) throw error
}

/* ── Reading ─────────────────────────────────────────────── */

export function elapsedMs(fast, now = Date.now()) {
  const start = new Date(fast.started_at).getTime()
  const end = fast.ended_at ? new Date(fast.ended_at).getTime() : now
  return Math.max(0, end - start)
}

export function hours(ms) { return ms / HOUR }

/** 14h 22m. Hours never roll into days: a 26-hour fast is 26h, not 1d 2h. */
export function duration(ms) {
  const total = Math.floor(ms / 60000)
  const h = Math.floor(total / 60)
  const m = total % 60
  return h + 'h ' + String(m).padStart(2, '0') + 'm'
}

export function clockTime(iso) {
  return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
}

/**
 * A fast counts towards the day it ENDED, which is the day he broke it and
 * the day he thinks of it as. A 6am-to-10pm fast is today's, not yesterday's.
 */
export function byDay(fasts, days = 14, today = new Date()) {
  const out = []
  const base = new Date(today)
  base.setHours(0, 0, 0, 0)

  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(base)
    d.setDate(d.getDate() - i)
    const key = d.toISOString().slice(0, 10)
    const mine = fasts.filter((f) => f.ended_at && localDay(f.ended_at) === key)
    const ms = mine.reduce((s, f) => s + elapsedMs(f), 0)
    out.push({
      key,
      date: d,
      hours: hours(ms),
      /* Met if the longest fast that day reached its own target, not the
         sum — two short fasts are not one long one. */
      met: mine.some((f) => hours(elapsedMs(f)) >= Number(f.target_hours)),
      count: mine.length,
    })
  }
  return out
}

function localDay(iso) {
  const d = new Date(iso)
  d.setHours(0, 0, 0, 0)
  const off = d.getTimezoneOffset() * 60000
  return new Date(d.getTime() - off).toISOString().slice(0, 10)
}

/** Average over days that had a fast. Days he did not fast are not zeroes. */
export function average(fasts, days = 7) {
  const rows = byDay(fasts, days).filter((d) => d.count > 0)
  if (!rows.length) return null
  return rows.reduce((s, d) => s + d.hours, 0) / rows.length
}
